<template>
  <div v-if="showAdviserList" ref="wrapper" class="available-advisers">
    <LoadingComponent
      v-if="loadingAdvisers || !hasPrescreeningBeenChecked"
      style="min-height: 300px"
    />
    <template v-else>
      <div class="available-advisers__header">
        <h2 class="section-title">
          <Badge :count="questionIndex" />
          {{ availableAdvisersTitle }}
        </h2>
        <AdviserRoleToggle v-if="shouldShowAdviserRoleToggle" />
      </div>
      <div
        v-if="prescreening"
        class="unavailable-selection-notification unavailable-selection-notification--adviser"
      >
        <div class="unavailable-selection-notification__icon">
          <AppIcon icon="warning" />
        </div>
        <div class="unavailable-selection-notification__text">
          <div
            v-dompurify-html="
              $t('appointment_form_page.available_advisers.unavailable', { adviser: prescreening })
            "
          />
        </div>
      </div>
      <AdviserCarouselContainer
        v-if="availableAdvisers.length > 0"
        :key="adviserCarouselContainerKey"
        :advisers="advisers"
        :selected-adviser="selectedAdviser"
        @selectAdviser="selectAdviser($event)"
      />
      <div
        v-else
        class="unavailable-selection-notification unavailable-selection-notification--adviser"
      >
        <div class="unavailable-selection-notification__icon">
          <AppIcon icon="warning" />
        </div>
        <div class="unavailable-selection-notification__text text-lg-nowrap">
          <p
            class="my-3 fs-5"
            v-html="$t('appointment_form_page.available_advisers.no_adviser_available')"
          ></p>
        </div>
      </div>
    </template>
  </div>
  <div v-else class="adviser-carousel-container" ref="wrapper"></div>
</template>

<script lang="ts">
import { usePage } from '@inertiajs/vue3'
import { defineComponent, PropType, ref, nextTick, useTemplateRef, onMounted } from 'vue'
import { Adviser } from '~concierge/types/AppointmentTypes'
import LoadingComponent from '~conciergeUiCommons/components/LoadingComponent.vue'
import Badge from '~conciergeUiCommons/components/Badge.vue'
import AdviserCarouselContainer from '~conciergeUiCommons/components/AppointmentQuestionComponents/AdviserCarouselContainer.vue'
import AdviserRoleToggle from '~conciergeUiCommons/components/AdviserRoleToggle.vue'
import { useIdentityStore } from '~conciergeUiCommons/store/identityStore'
import { IdentityData, IdentitySettings } from '~concierge/types/IdentityTypes'

export default defineComponent({
  name: 'AvailableAdvisers',
  inheritAttrs: false,
  components: {
    LoadingComponent,
    Badge,
    AdviserCarouselContainer,
    AdviserRoleToggle
  },
  props: {
    availableAdvisers: {
      type: Array as PropType<Adviser[]>,
      required: true
    },
    selectedAdviser: {
      type: Object as PropType<Adviser | null>,
      required: false
    },
    changeAdviser: {
      type: Boolean,
      default: false
    },
    loadingAdvisers: {
      type: Boolean,
      default: false
    },
    hasPrescreeningBeenChecked: {
      type: Boolean,
      default: false
    },
    questionIndex: {
      type: Number,
      required: true
    },
    prescreening: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showUsedAdviser: {
      type: Boolean,
      default: false
    },
    showAdviserList: {
      type: Boolean,
      default: true,
      required: false
    }
  },
  setup(props, { expose }) {
    const wrapper = useTemplateRef('wrapper')
    const loaderHeight = ref<number>(100)
    const identityStore = useIdentityStore()
    const showAdviserRoleToggle = usePage().props.showAdviserRoleToggle
    const adviserRoleSlug = (usePage().props.adviserRoleSlug as string) || ''
    async function scrollToViewport() {
      if (wrapper.value && wrapper.value.clientHeight > loaderHeight.value) {
        loaderHeight.value = wrapper.value?.clientHeight
      }
      await nextTick()
      await new Promise(resolve => setTimeout(resolve, 500))
      await nextTick(async () => {
        await wrapper.value?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        })
      })
    }
    expose({ scrollToViewport })
    return {
      showAdviserRoleToggle,
      adviserRoleSlug,
      identityStore,
      loaderHeight,
      wrapper,
      scrollToViewport
    }
  },
  emits: ['selectAdviser'],
  computed: {
    availableAdvisersTitle(): string {
      if (this.$page.props.isUsedVehicle) {
        return this.$t('appointment_form_page.available_advisers.title_for_vehicle')
      }
      return this.$t('appointment_form_page.available_advisers.title')
    },
    identity(): IdentityData | null {
      return this.identityStore.identity as IdentityData
    },
    settings(): IdentitySettings | null {
      return this.identityStore.identity?.settings as IdentitySettings
    },
    isConciergeEmbedContext(): boolean {
      return this.identityStore.service === 'conciergeEmbed'
    },
    shouldShowAdviserRoleToggle(): boolean {
      return (
        this.isConciergeEmbedContext &&
        this.showAdviserRoleToggle &&
        (this.settings?.usedDepartmentAdvisersEnabled ?? false)
      )
    },
    roleToShow(): string {
      return this.adviserRoleSlug || (this.showUsedAdviser ? 'used' : 'new')
    },
    adviserCarouselContainerKey(): string {
      return this.availableAdvisers.length + '_' + this.roleToShow
    },
    advisers() {
      return this.availableAdvisers
        .filter(adviser => {
          return adviser.roles.includes(this.roleToShow)
        })
        .map(adviser => {
          return {
            roles: adviser.roles,
            id: adviser.id,
            name: `${adviser.first_name} ${adviser.last_name}`,
            info: adviser.occupation,
            photo: adviser.photo,
            photo_large: adviser.photo_large,
            spoken_languages: adviser.spoken_languages,
            biography: adviser.biography,
            videos: adviser.videos,
            reviews: adviser.reviews
          }
        })
    }
  },
  methods: {
    selectAdviser(adviser: Record<string, string | number>) {
      if (this.disabled) {
        return
      }
      const selectAdviser = this.availableAdvisers.find(x => x.id === adviser.id)
      this.$emit('selectAdviser', selectAdviser)
    }
  }
})
</script>
<style scoped lang="scss">
.section-title {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 10px;
  place-items: flex-end;
}
</style>
